/**
 * Optimized Image Component
 * Wrapper around Next.js Image with local caching support
 * Provides faster image loading through Cache API
 */

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import {
  getCachedImage,
  cacheImage,
  isImageCached,
  removeCachedImage,
} from '@/services/imageCacheService';

export interface OptimizedImageProps {
  src: string;
  alt: string;
  fill?: boolean;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean;
  loading?: 'lazy' | 'eager';
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
  blurDataURL?: string;
  quality?: number;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  /**
   * If true, always use original URL instead of blob URLs from cache
   * Useful for debugging or when blob URLs are causing issues
   * @default false
   */
  disableBlobCache?: boolean;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  fill,
  width,
  height,
  sizes,
  priority = false,
  loading = 'lazy',
  className,
  style,
  onLoad,
  onError,
  blurDataURL,
  quality = 90,
  objectFit = 'cover',
  disableBlobCache = false,
}) => {
  const [cachedSrc, setCachedSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [shouldUseCache, setShouldUseCache] = useState(true);
  const cacheCheckDoneRef = useRef(false);
  const previousSrcRef = useRef<string | null>(null);

  // Check cache and load image
  useEffect(() => {
    // Reset state if src changed
    if (previousSrcRef.current !== src) {
      previousSrcRef.current = src;
      cacheCheckDoneRef.current = false;
      setIsLoading(true);
      setHasError(false);
      setShouldUseCache(true);
      // Clean up previous blob URL
      if (cachedSrc && cachedSrc.startsWith('blob:')) {
        URL.revokeObjectURL(cachedSrc);
        setCachedSrc(null);
      }
    }

    if (!src || cacheCheckDoneRef.current) return;

    let isMounted = true;
    let currentBlobUrl: string | null = null;

    const loadImage = async () => {
      try {
        // If blob cache is disabled, always use original URL
        if (disableBlobCache) {
          if (isMounted) {
            setCachedSrc(null); // Use original src
            setIsLoading(false);
            cacheCheckDoneRef.current = true;

            // Still cache in background for future use (even if we don't use blob URLs)
            cacheImage(src).catch(() => {
              // Silently fail - background caching errors are expected and non-critical
            });
          }
          return;
        }

        // Check if image is cached
        const cached = await isImageCached(src);
        if (cached && isMounted) {
          const blobUrl = await getCachedImage(src);
          if (blobUrl && isMounted) {
            // Validate blob URL before using it
            try {
              // Quick validation: check if it's a valid blob URL format
              const testUrl = new URL(blobUrl);
              if (testUrl.protocol !== 'blob:') {
                throw new Error('Invalid blob URL format');
              }

              currentBlobUrl = blobUrl;
              setCachedSrc(blobUrl);
              setIsLoading(false);
              cacheCheckDoneRef.current = true;

              if (process.env.NODE_ENV === 'development') {
                console.log(
                  '[OptimizedImage] Using cached blob URL:',
                  blobUrl.substring(0, 50) + '...',
                  'for original:',
                  src.substring(0, 50) + '...'
                );
              }
              return;
            } catch (error) {
              // Blob URL is invalid, remove from cache and use original
              if (process.env.NODE_ENV === 'development') {
                console.warn(
                  '[OptimizedImage] Invalid blob URL from cache, removing and using original:',
                  src.substring(0, 50) + '...',
                  error
                );
              }
              await removeCachedImage(src).catch(() => {});
              // Fall through to use original URL
            }
          }
        }

        // If not cached or cache failed, use original URL
        // Cache the image in the background for next time
        if (isMounted) {
          setCachedSrc(null); // Use original src
          setIsLoading(false);
          cacheCheckDoneRef.current = true;

          // Cache in background (don't wait for it)
          // Errors are silently handled - caching is non-critical
          cacheImage(src).catch(() => {
            // Silently fail - background caching errors are expected and non-critical
          });
        }
      } catch (error) {
        // Cache errors are non-critical - fallback to original image
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[OptimizedImage] Error loading image (falling back to original):',
            error
          );
        }
        if (isMounted) {
          setCachedSrc(null); // Fallback to original
          setIsLoading(false);
          setShouldUseCache(false);
          cacheCheckDoneRef.current = true;
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
      // Clean up blob URL if we created one
      // Only revoke if it's still the current blob URL (prevent double-revocation)
      if (
        currentBlobUrl &&
        currentBlobUrl.startsWith('blob:') &&
        blobUrlRef.current === currentBlobUrl
      ) {
        try {
          URL.revokeObjectURL(currentBlobUrl);
          blobUrlRef.current = null;
        } catch {
          // Blob URL may already be revoked, ignore
        }
      }
    };
  }, [src, cachedSrc, disableBlobCache]);

  // Cleanup blob URL on unmount or when src changes
  // Use a ref to track blob URLs to prevent premature revocation in React Strict Mode
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (cachedSrc && cachedSrc.startsWith('blob:')) {
      blobUrlRef.current = cachedSrc;
    } else {
      blobUrlRef.current = null;
    }

    return () => {
      // Only revoke if this is still the current blob URL
      // This prevents double-revocation in React Strict Mode
      if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        } catch {
          // Blob URL may already be revoked, ignore
        }
      }
    };
  }, [cachedSrc]);

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleError = () => {
    // If blob URL failed, immediately fallback to original URL
    if (cachedSrc && cachedSrc.startsWith('blob:')) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[OptimizedImage] Blob URL failed (ERR_FILE_NOT_FOUND), immediately falling back to original URL:',
          src
        );
      }

      // Safely revoke the failed blob URL (may already be revoked)
      try {
        URL.revokeObjectURL(cachedSrc);
      } catch {
        // Blob URL may already be revoked, ignore error
      }

      // Immediately switch to original URL
      setCachedSrc(null);
      setShouldUseCache(false);
      setIsLoading(true); // Reset loading to retry with original URL
      setHasError(false); // Reset error to allow retry

      // Remove invalid cache entry in background (don't wait for it)
      removeCachedImage(src).catch(() => {
        // Silently fail - cleanup is non-critical
      });

      // Don't call onError yet - let it retry with original URL
      return;
    }

    // If original URL also failed, then show error
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  // Validate blob URL before using it
  // Blob URLs can become invalid if revoked or if the blob is corrupted
  const validateBlobUrl = (url: string | null): boolean => {
    if (!url || !url.startsWith('blob:')) return true; // Not a blob URL, assume valid

    try {
      // Try to create a test request to see if blob URL is still valid
      // This is a lightweight check that doesn't actually fetch the blob
      const testUrl = new URL(url);
      return testUrl.protocol === 'blob:';
    } catch {
      // Invalid URL format
      return false;
    }
  };

  // Use cached blob URL if available and valid, otherwise use original src
  const imageSrc = cachedSrc && validateBlobUrl(cachedSrc) ? cachedSrc : src;

  // If cache failed, blob URL failed, or blob URL is invalid, use original src directly
  // Never use blob URLs if they're invalid - always fallback to original
  const finalSrc =
    shouldUseCache && !hasError && validateBlobUrl(cachedSrc) ? imageSrc : src;

  return (
    <div
      style={{
        position: fill ? 'relative' : 'static',
        width: fill ? '100%' : width,
        height: fill ? '100%' : height,
        ...style,
      }}
      className={className}
    >
      {/* Loading placeholder */}
      {isLoading && !hasError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#E5E7EB',
            backgroundImage:
              'linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
          className='animate-pulse'
        >
          <div
            style={{
              width: '24px',
              height: '24px',
              border: '2px solid #f3f4f6',
              borderTop: '2px solid #3b82f6',
              borderRadius: '50%',
            }}
            className='animate-spin'
          />
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <p
            style={{
              fontSize: '12px',
              color: '#6b7280',
              textAlign: 'center',
              margin: 0,
              padding: '8px',
            }}
          >
            Failed to load
          </p>
        </div>
      )}

      {/* Actual image */}
      {!hasError && (
        <Image
          key={finalSrc} // Force re-render when switching from blob to original URL
          src={finalSrc}
          alt={alt}
          fill={fill}
          width={fill ? undefined : width}
          height={fill ? undefined : height}
          sizes={sizes}
          priority={priority}
          loading={loading}
          quality={quality}
          style={{
            objectFit,
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out',
            ...(style || {}),
          }}
          className={className}
          onLoad={handleLoad}
          onError={handleError}
          blurDataURL={blurDataURL}
          unoptimized={true} // Keep unoptimized as per current config
        />
      )}
    </div>
  );
};
